/**
 * Cloudflare Workers AI Client
 * Handles all interactions with Cloudflare's AI models
 */

import { DEFAULT_MODELS, getModelById } from '../config/cloudflare-models.js';

export class CloudflareAIClient {
  constructor(accountId, apiToken, workerUrl) {
    this.accountId = accountId;
    this.apiToken = apiToken;
    // Use worker URL if provided, otherwise use direct API (requires CORS proxy)
    this.baseUrl = workerUrl || import.meta.env.VITE_WORKER_URL || `https://api.cloudflare.com/client/v4/accounts/${accountId}/ai/run`;
    this.isWorkerMode = !!workerUrl || !!import.meta.env.VITE_WORKER_URL;
  }

  /**
   * Run inference with a specific model
   */
  async runInference(modelId, messages, options = {}) {
    const model = getModelById(modelId) || DEFAULT_MODELS.primary;

    try {
      // Build request URL and headers
      const url = this.isWorkerMode
        ? `${this.baseUrl}/ai/run/${model.id}`
        : `${this.baseUrl}/${model.id}`;

      const headers = this.isWorkerMode
        ? { 'Content-Type': 'application/json' }
        : {
            'Authorization': `Bearer ${this.apiToken}`,
            'Content-Type': 'application/json'
          };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages,
          stream: options.stream || false,
          max_tokens: options.maxTokens || model.contextWindow,
          temperature: options.temperature || 0.7,
          top_p: options.topP || 0.9,
          ...options
        })
      });

      if (!response.ok) {
        const error = await response.json();
        const errorMessage = error.error || error.message || error.errors?.[0]?.message || response.statusText;
        throw new Error(`Cloudflare AI API Error: ${errorMessage}`);
      }

      if (options.stream) {
        return this.handleStreamResponse(response);
      }

      const data = await response.json();
      return data.result;
    } catch (error) {
      console.error('Cloudflare AI inference error:', error);
      throw error;
    }
  }

  /**
   * Handle streaming response
   */
  async *handleStreamResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim() || !line.startsWith('data: ')) continue;

          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.response) {
              yield parsed.response;
            }
          } catch (e) {
            console.warn('Failed to parse stream chunk:', e);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Generate embeddings for text
   */
  async generateEmbeddings(text, modelId = DEFAULT_MODELS.embeddings.id) {
    try {
      const url = this.isWorkerMode
        ? `${this.baseUrl}/ai/run/${modelId}`
        : `${this.baseUrl}/${modelId}`;

      const headers = this.isWorkerMode
        ? { 'Content-Type': 'application/json' }
        : {
            'Authorization': `Bearer ${this.apiToken}`,
            'Content-Type': 'application/json'
          };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        const error = await response.json();
        const errorMessage = error.error || error.message || response.statusText;
        throw new Error(`Embeddings API Error: ${errorMessage}`);
      }

      const data = await response.json();

      // Handle different response formats
      if (data.result?.data?.[0]) {
        return Array.from(data.result.data[0]); // Ensure it's a plain array
      } else if (data.result?.values) {
        return Array.from(data.result.values);
      } else if (Array.isArray(data.result)) {
        return data.result;
      }

      throw new Error('Unexpected embeddings response format');
    } catch (error) {
      console.error('Cloudflare embeddings error:', error);
      throw error;
    }
  }

  /**
   * Batch embeddings generation
   */
  async generateBatchEmbeddings(texts, modelId = DEFAULT_MODELS.embeddings.id) {
    const embeddings = await Promise.all(
      texts.map(text => this.generateEmbeddings(text, modelId))
    );
    return embeddings;
  }

  /**
   * Test connection to Cloudflare AI
   */
  async testConnection() {
    try {
      const response = await this.runInference(
        DEFAULT_MODELS.fast.id,
        [{ role: 'user', content: 'Hello! Please respond with "OK" if you can hear me.' }],
        { maxTokens: 10 }
      );
      return { success: true, response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Estimate token count (approximate)
   */
  estimateTokens(text) {
    // Rough estimation: ~4 characters per token for English
    return Math.ceil(text.length / 4);
  }

  /**
   * Calculate cost estimate for a request
   */
  estimateCost(tokenCount, model = DEFAULT_MODELS.primary) {
    // Cloudflare Workers AI pricing (as of 2024)
    // Free tier: 10,000 neurons per day
    // Paid: $0.01 per 1,000 neurons
    const neurons = tokenCount;
    const costPer1000 = 0.01;
    return (neurons / 1000) * costPer1000;
  }
}

/**
 * Create a singleton instance
 */
let clientInstance = null;

export function initializeCloudflareClient(accountId, apiToken, workerUrl) {
  clientInstance = new CloudflareAIClient(accountId, apiToken, workerUrl);
  return clientInstance;
}

export function getCloudflareClient() {
  if (!clientInstance) {
    throw new Error('Cloudflare AI client not initialized. Call initializeCloudflareClient first.');
  }
  return clientInstance;
}
